import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, CheckCircle2, Circle, UploadCloud, FileCheck2, PartyPopper, Loader2, Briefcase, ExternalLink, Award, CreditCard,
} from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import { ONBOARDING_PHASES, ONBOARDING_TOTAL } from "../../lib/onboardingConfig";
import api, { API, getToken } from "../../lib/api";

const CERT_PHASES = ["phase2", "phase4", "phase5"];
function certUrl(key) { return `${API}/application/me/certificate/${key}?auth=${getToken()}`; }

function ItemRow({ item, state, onToggle, onUpload, certEligible, payment }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const done = !!state?.completed;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await onUpload(item.id, fd);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#E5E2DC] bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`ob-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => onToggle(item.id, !done)} className="mt-0.5 shrink-0" data-testid={`ob-toggle-${item.id}`}>
          {done ? <CheckCircle2 className="h-6 w-6 text-[#3A7D5E]" /> : <Circle className="h-6 w-6 text-[#C7C2B8]" />}
        </button>
        <div>
          <div className="font-medium text-[#1F2924]">{item.title}</div>
          <div className="text-sm text-[#515E58]">{item.desc}</div>
          {item.resources && (
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
              {item.resources.map(([label, url]) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#2C5545] hover:underline"
                  data-testid={`ob-resource-${item.id}`}>
                  <ExternalLink className="h-3 w-3" /> {label}
                </a>
              ))}
            </div>
          )}
          {state?.filename && <div className="mt-1 inline-flex items-center gap-1 text-xs text-[#3A7D5E]"><FileCheck2 className="h-3 w-3" /> {state.filename}</div>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 pl-9 sm:pl-0">
        {payment && (
          <Button size="sm" onClick={() => window.open(payment.url, "_blank")}
            data-testid={`ob-pay-${item.id}`} className="gap-2 bg-[#D56A47] text-white hover:bg-[#c25a39]">
            <CreditCard className="h-4 w-4" /> {payment.label || `Pay ${payment.amount} Fee`}
          </Button>
        )}
        {certEligible && state?.completed && (
          <Button variant="outline" size="sm" onClick={() => window.open(certUrl(item.id), "_blank")}
            data-testid={`ob-cert-${item.id}`} className="gap-2 border-[#3A7D5E]/40 text-[#2C5545] hover:bg-[#EAF3EE]">
            <Award className="h-4 w-4" /> Certificate
          </Button>
        )}
        {item.upload && (
          <>
            <input ref={inputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={handleFile} data-testid={`ob-file-${item.id}`} />
            <Button variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}
              data-testid={`ob-upload-${item.id}`} className="gap-2 border-[#E5E2DC] text-[#515E58] hover:bg-[#F4F2EC]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload
            </Button>
          </>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-sm text-[#515E58]">
          <Checkbox checked={done} onCheckedChange={(v) => onToggle(item.id, !!v)} data-testid={`ob-check-${item.id}`} />
          Done
        </label>
      </div>
    </div>
  );
}

export default function OnboardingPortal() {
  const [state, setState] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/application/me/onboarding");
      setState(data);
    } catch (e) {
      if (e.response?.status === 403) navigate("/portal", { replace: true });
      else toast.error("Could not load onboarding.");
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id, completed) => {
    try { const { data } = await api.post(`/application/me/onboarding/item/${id}`, { completed }); setState(data); }
    catch { toast.error("Could not update"); }
  };
  const upload = async (id, fd) => {
    const { data } = await api.post(`/application/me/onboarding/item/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setState(data.state);
  };

  const items = state?.items || {};
  const doneCount = useMemo(() => Object.values(items).filter((v) => v.completed).length, [items]);
  const pct = Math.round((doneCount / ONBOARDING_TOTAL) * 100);
  const complete = doneCount >= ONBOARDING_TOTAL;

  if (!state) return <div className="min-h-screen bg-[#FAF9F6]" />;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/portal")} className="mb-4 inline-flex items-center gap-2 text-sm text-[#515E58] hover:text-[#1F2924]" data-testid="ob-back">
          <ArrowLeft className="h-4 w-4" /> Back to portal
        </button>

        <div className="rounded-2xl bg-[#2C5545] p-8 text-white">
          <div className="flex items-center gap-3">
            <Briefcase className="h-7 w-7" />
            <h1 className="font-heading text-2xl font-bold sm:text-3xl">New Hire Onboarding</h1>
          </div>
          <p className="mt-2 max-w-2xl text-white/85">
            Welcome to GIC&E! Track your onboarding progress below. Complete each phase step by step — you've got this.
          </p>
          <div className="mt-5">
            <div className="mb-1 flex justify-between text-sm text-white/80">
              <span>{doneCount} of {ONBOARDING_TOTAL} items completed</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-[#D56A47] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {complete && (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-6 rounded-2xl border border-[#3A7D5E]/30 bg-[#EEF5F0] p-6 text-center" data-testid="ob-complete">
            <PartyPopper className="mx-auto h-10 w-10 text-[#3A7D5E]" />
            <h2 className="mt-3 font-heading text-xl font-bold text-[#1F2924]">Onboarding Complete!</h2>
            <p className="mt-1 text-[#515E58]">You've completed every onboarding item. Welcome to the GIC&E community.</p>
          </motion.div>
        )}

        <div className="mt-6">
          <Accordion type="multiple" defaultValue={["phase1"]} className="space-y-3">
            {ONBOARDING_PHASES.map((ph) => {
              const Icon = ph.icon;
              const phaseDone = ph.items.filter((it) => items[it.id]?.completed).length;
              const phaseComplete = phaseDone === ph.items.length;
              return (
                <AccordionItem key={ph.id} value={ph.id}
                  className="overflow-hidden rounded-xl border border-[#E5E2DC] bg-white px-0" data-testid={`ob-phase-${ph.id}`}>
                  <AccordionTrigger className="px-5 hover:no-underline">
                    <div className="flex flex-1 items-center gap-3 text-left">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${phaseComplete ? "bg-[#EAF3EE] text-[#3A7D5E]" : "bg-[#EAF0ED] text-[#2C5545]"}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-heading text-base font-semibold text-[#1F2924]">
                          {ph.title} {phaseComplete && <span className="ml-2 rounded-full bg-[#EAF3EE] px-2 py-0.5 text-xs font-medium text-[#3A7D5E]">Complete</span>}
                        </div>
                        <div className="text-xs text-[#515E58]">{phaseDone} of {ph.items.length} items</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <p className="mb-4 text-sm text-[#515E58]">{ph.blurb}</p>
                    <div className="space-y-3">
                      {ph.items.map((it) => (
                        <ItemRow key={it.id} item={it} state={items[it.id]} onToggle={toggle} onUpload={upload}
                          certEligible={CERT_PHASES.includes(ph.id)} payment={state.payments?.[it.id]} />
                      ))}
                    </div>
                    {ph.id === "phase3" && items["prob_approved"]?.completed && (
                      <div className="mt-4 flex items-center justify-between rounded-xl border border-[#3A7D5E]/30 bg-[#EEF5F0] p-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-[#2C5545]">
                          <Award className="h-5 w-5" /> Approved to Work with GIC&E
                        </div>
                        <Button variant="outline" size="sm" onClick={() => window.open(certUrl("phase3"), "_blank")}
                          data-testid="ob-cert-phase3" className="gap-2 border-[#3A7D5E]/40 text-[#2C5545] hover:bg-white">
                          <Award className="h-4 w-4" /> View Certificate
                        </Button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </main>
    </div>
  );
}
